// PrivacyPolicy.js
import React from 'react';
import { Container, Typography, Accordion, AccordionSummary, AccordionDetails, Box, Card, CardContent } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Footer from '../../components/footer/Footer';
import SEO from '../../components/SEO/SEO';
import Header from '../../components/header/Header';

const PrivacyPolicy = () => {
    return (
        <div>
            <SEO
                title="Privacy Policy - ManageEcom | Data Protection & Security"
                description="ManageEcom's privacy policy explains how we collect, use, and protect your data. Learn about our commitment to your privacy and data security."
                keywords="privacy policy, data protection, manageecom privacy, user data security, gdpr compliance, data privacy"
                canonical={process.env.REACT_APP_MANAGEECOM_BASE_URL + "/privacy_policy"}
            />
            <Header />
            <div className='back-small-div' />
            <Container className="back-small-div-container mb-4">
                <div className='center mt-5'>
                    <div >
                        <h1 className="text-light text-center"><b>Privacy Policy</b></h1>
                        <h6 className='text-light text-center'>Effective Date: 06-09-2024</h6>
                    </div>
                </div>
            </Container>

            <Container maxWidth="md" style={{ marginTop: '80px', marginBottom: '20px' }}>
                <Card elevation={3} style={{ padding: '20px' }}>
                    <CardContent>
                        <Typography variant="body1" paragraph>
                            At ManageEcom, we are committed to protecting the privacy and security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
                        </Typography>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">1. Information We Collect</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    We collect information that you provide directly to us, including:
                                    <ul>
                                        <li>Personal Information: Name, email address, phone number, and business details when you register for an account.</li>
                                        <li>Payment Information: Billing details and payment card information when you subscribe to our services.</li>
                                        <li>Usage Data: Information about how you use our platform, including logs, analytics, and performance data.</li>
                                        <li>Communications: Any correspondence you have with us, including support requests and feedback.</li>
                                    </ul>
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">2. How We Use Your Information</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    We use the information we collect to:
                                    <ul>
                                        <li>Provide, maintain, and improve our services.</li>
                                        <li>Process transactions and send related information, including confirmations and invoices.</li>
                                        <li>Respond to your comments, questions, and provide customer support.</li>
                                        <li>Monitor and analyze trends, usage, and activities in connection with our services.</li>
                                        <li>Send you technical notices, updates, security alerts, and support messages.</li>
                                        <li>Comply with legal obligations and enforce our terms and conditions.</li>
                                    </ul>
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">3. Information Sharing and Disclosure</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    We may share your information in the following situations:
                                    <ul>
                                        <li>With Service Providers: We may share your information with third-party vendors who perform services on our behalf.</li>
                                        <li>For Legal Reasons: We may disclose your information if required by law or in response to legal requests.</li>
                                        <li>Business Transfers: In the event of a merger, acquisition, or sale of assets, your information may be transferred.</li>
                                        <li>With Your Consent: We may share your information with your consent or at your direction.</li>
                                    </ul>
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">4. Data Security</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">5. Data Retention</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">6. Your Rights</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    Depending on your location, you may have certain rights regarding your personal information, including:
                                    <ul>
                                        <li>The right to access and receive a copy of your personal information.</li>
                                        <li>The right to request correction of inaccurate information.</li>
                                        <li>The right to request deletion of your personal information.</li>
                                        <li>The right to object to or restrict certain processing of your information.</li>
                                        <li>The right to data portability.</li>
                                    </ul>
                                    To exercise these rights, please contact us at support@nuvio.in.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">7. Cookies and Tracking Technologies</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    We use cookies and similar tracking technologies to collect and track information about your use of our services. You can control cookies through your browser settings and other tools.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">8. Third-Party Links</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    Our services may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">9. Children's Privacy</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">10. Changes to This Privacy Policy</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the effective date.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">11. Contact Us</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    If you have any questions about this Privacy Policy, please contact us at:
                                    <br /><br />
                                    Nuvio Technologies Private Limited<br />
                                    Shop No. 2, Shakti Dham CHS, Plot No. 125, Sector - 21, Ghansoli, Navi Mumbai - 400701, Maharashtra, India<br />
                                    Email: support@nuvio.in<br />
                                    Phone: +91 7718895734
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    </CardContent>
                </Card>
            </Container>
            <Footer />
        </div>
    );
};

export default PrivacyPolicy;
